import { baseUrl } from './../../utils/axios'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { handleErrors } from 'utils/helpers'
import { IHrDashboardSlice } from 'types/hrDashboard.interface'
import { AxiosResponse } from 'axios'
import {
  IGetAllHrDashboardAiSuggestionsSingleResponse,
  IGetAllHrDashboardNotificationsSingleReponse,
} from 'types/api/hrDashboardApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'

const initialState: IHrDashboardSlice = {
  isLoading: false,
  accommodationSummary: null,
  shiftsSummary: null,
  attendanceSummary: null,
  openHRRequestCount: 0,
  aiSuggestions: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  notifications: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
}

export const getHrDashboardData = createAsyncThunk(
  'hrDashboard/getHrDashboardData',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<Omit<IHrDashboardSlice, 'isLoading'>> = await baseUrl.get(
        'hr/dashboard/info',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getHrDashboardNotifications = createAsyncThunk(
  'hrDashboard/getHrDashboardNotifications',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllHrDashboardNotificationsSingleReponse>> =
        await baseUrl.get('hr/dashboard/notification', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getHrDashboardAiSuggestions = createAsyncThunk(
  'hrDashboard/getHrDashboardAiSuggestions',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllHrDashboardAiSuggestionsSingleResponse>> =
        await baseUrl.get('hr/dashboard/ai-suggestions', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const hrDashboardSlice = createSlice({
  name: 'hrDashboard',
  initialState,
  reducers: {
    clearHrDashboard: (state) => {
      state.accommodationSummary = null
      state.attendanceSummary = null
      state.shiftsSummary = null
      state.openHRRequestCount = 0
      state.notifications = {
        data: [],
        totalPages: 0,
        totalCount: 0,
      }
    },
  },
  extraReducers: {
    [getHrDashboardData.pending.type]: (state) => {
      state.isLoading = true
    },
    [getHrDashboardData.fulfilled.type]: (
      state,
      { payload }: { payload: Omit<IHrDashboardSlice, 'isLoading'> },
    ) => {
      state.isLoading = false
      state.accommodationSummary = payload.accommodationSummary
      state.attendanceSummary = payload.attendanceSummary
      state.shiftsSummary = payload.shiftsSummary
      state.openHRRequestCount = payload.openHRRequestCount ?? 0
    },
    [getHrDashboardData.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getHrDashboardNotifications.pending.type]: (state) => {
      state.isLoading = true
    },
    [getHrDashboardNotifications.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllHrDashboardNotificationsSingleReponse> },
    ) => {
      state.isLoading = false
      state.notifications = payload
    },
    [getHrDashboardNotifications.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getHrDashboardAiSuggestions.pending.type]: (state) => {
      state.isLoading = true
    },
    [getHrDashboardAiSuggestions.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllHrDashboardAiSuggestionsSingleResponse> },
    ) => {
      state.isLoading = false
      state.aiSuggestions = payload
    },
    [getHrDashboardAiSuggestions.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export default hrDashboardSlice.reducer
export const { clearHrDashboard } = hrDashboardSlice.actions
